// rescontrolcambios.actions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  Responsable,
  ResponsableForm,
  ResponsableParams,
  ResponsablePagingResp,
} from "@/erp/controlcambios/responsable-controlcambios/types/rescontrolcambios.interface";
import { useFetchUsuariosSistemaPorArreglo } from "../erp/administration/usuarios";

const { get, post, put, remove, patch } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/responsables`;

// listar
export const useFetchGetResponsables = (
  params?: ResponsableParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["responsables", ...Object.values(params ?? {})],
    queryFn: async () => getResponsables(params),
    enabled,
  });

export const getResponsables = async (params?: ResponsableParams) => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<ResponsablePagingResp>(`${BASE}?${q}`, true)) as unknown as ResponsablePagingResp;
};

// obtener por id
export const useFetchGetByIdResponsable = (id: string) =>
  useQuery({
    queryKey: ["responsable", id],
    queryFn: async () => getByIdResponsable(id),
    refetchOnWindowFocus: false,
  });

export const getByIdResponsable = async (id: string) =>
  await get<Responsable>(`${BASE}/${id}`, true);

// crear
export const useFetchPostResponsable = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: ResponsableForm) => postResponsable(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["responsables"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Responsable creado correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postResponsable = async (data: ResponsableForm) =>
  await post<Responsable>(BASE, data, true);

// actualizar
export const useFetchPutResponsable = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putResponsable,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["responsables"] });
      queryClient.invalidateQueries({ queryKey: ["responsable", variables.id] });
      setAlerta(true, "Responsable actualizado correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putResponsable = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<ResponsableForm, "responsableId">;
}) => await put<Responsable>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteResponsable = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteResponsable(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["responsables"] });
      setAlerta(true, "Responsable eliminado correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteResponsable = async (id: string) =>
  await remove<Responsable>(`${BASE}/${id}`, true);




export const useResponsablesConNombre = () => {
  const responsablesQuery = useFetchGetResponsables({});
  const responsables = responsablesQuery.data?.data ?? []; // PageResponse -> data.data
  const ids = responsables.map(r => r.usuarioId);
  
  
  ///
  const usuariosQuery = useFetchUsuariosSistemaPorArreglo({
    enabled: responsablesQuery.isSuccess && ids.length > 0,
    params: { usuarios: ids, paginate: false },
  });

   const usuarios = usuariosQuery.data?.data.data ?? [];

  const responsablesConNombre = responsables.map(r => {
    const usuario = usuarios.find((u: any) => u.id === r.usuarioId);
    console.log(usuario);
    return {
      responsableId: r.responsableId,
      usuarioId: r.usuarioId,
      nombre: usuario?.empleado?.razon_social || usuario?.username || usuario?.email || `Usuario no encontrado`,
      cargo: r.cargo,
      correo: r.correo,
    };
  });
 
  return {
    responsables: responsablesConNombre,
    isLoading: responsablesQuery.isLoading || usuariosQuery.isLoading,
  };
};


export const patchEstadoResponsable = async ({ id, estado }: { id: string; estado: boolean }) =>
  await patch(`${BASE}/${id}/estado?estado=${estado}`, undefined, true);

export const useFetchPatchEstadoResponsable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEstadoResponsable,
    onSuccess: () => {
      toast.success("Estado del Responsable actualizado");
      queryClient.invalidateQueries({ queryKey: ["responsables"] });
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};